import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  History,
  Search,
  Calendar,
  DollarSign,
  Users,
  CheckCircle2,
  Clock,
  Download,
  RefreshCw,
  Mail,
  Phone,
  Store,
  UserCheck,
} from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

const formatIQD = (n) =>
  new Intl.NumberFormat("ar-IQ").format(Math.round(n || 0)) + " د.ع";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-IQ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function ActivationHistoryPage() {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | partial

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/license/admin/activation-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.history || []);
      setStats(res.data.stats || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ في جلب السجل");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter((h) => {
      if (statusFilter !== "all" && h.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        h.user_email,
        h.user_name,
        h.user_phone,
        h.shop_name,
        h.activated_by,
        h.activated_by_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [history, search, statusFilter]);

  const exportCSV = () => {
    if (!filtered.length) {
      toast.error("لا توجد بيانات للتصدير");
      return;
    }
    const header = [
      "التاريخ",
      "اسم العميل",
      "البريد",
      "الهاتف",
      "اسم المتجر",
      "نوع الترخيص",
      "المبلغ المدفوع",
      "الحالة",
      "فعّل بواسطة",
    ];
    const rows = filtered.map((h) => [
      formatDate(h.activated_at),
      h.user_name || "",
      h.user_email || "",
      h.user_phone || "",
      h.shop_name || "",
      h.license_name || h.license_type || "",
      h.paid_amount || 0,
      h.status === "active" ? "كامل" : "جزئي",
      h.activated_by_name || h.activated_by || "",
    ]);
    const csv =
      "\ufeff" +
      [header, ...rows]
        .map((r) =>
          r
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activation-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("تم تنزيل الملف");
  };

  return (
    <div className="space-y-6" data-testid="activation-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <History className="w-6 h-6 text-emerald-400" />
            سجل التفعيلات
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            كل عمليات تفعيل الحسابات اليدوية مع التواريخ والمبالغ والمسؤول
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-slate-600 text-slate-200 hover:bg-slate-700"
            data-testid="refresh-history-btn"
          >
            <RefreshCw className="w-4 h-4 me-2" />
            تحديث
          </Button>
          <Button
            onClick={exportCSV}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-history-csv-btn"
          >
            <Download className="w-4 h-4 me-2" />
            تصدير CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400">إجمالي التفعيلات</div>
                <div className="text-xl font-bold text-white" data-testid="stat-total-activations">
                  {stats.total_activations}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400">إجمالي الإيرادات</div>
                <div className="text-lg font-bold text-white" data-testid="stat-total-revenue">
                  {formatIQD(stats.total_revenue)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400">دفع كامل</div>
                <div className="text-xl font-bold text-white">
                  {stats.full_activations}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400">دفع جزئي</div>
                <div className="text-xl font-bold text-white">
                  {stats.partial_activations}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="ابحث بالبريد، الاسم، الهاتف، اسم المتجر..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white pr-10"
              data-testid="history-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
            data-testid="history-status-filter"
          >
            <option value="all">كل الحالات</option>
            <option value="active">دفع كامل</option>
            <option value="partial">دفع جزئي</option>
          </select>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center justify-between">
            <span>السجل ({filtered.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-12">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-12" data-testid="history-empty">
              لا توجد تفعيلات مسجّلة بعد. كل تفعيل تقوم به من صفحة إدارة التراخيص سيظهر هنا تلقائياً.
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((h) => (
                <div
                  key={h.id}
                  className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/30 transition"
                  data-testid={`history-row-${h.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    {/* Left: User info */}
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white">
                          {h.user_name || "بدون اسم"}
                        </span>
                        <Badge
                          className={
                            h.status === "active"
                              ? "bg-emerald-500 text-white"
                              : "bg-amber-500 text-white"
                          }
                        >
                          {h.status === "active" ? "كامل" : "جزئي"}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {h.license_name || h.license_type}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        {h.user_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {h.user_email}
                          </span>
                        )}
                        {h.user_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {h.user_phone}
                          </span>
                        )}
                        {h.shop_name && (
                          <span className="flex items-center gap-1">
                            <Store className="w-3 h-3" />
                            {h.shop_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Amount + Meta */}
                    <div className="flex flex-col items-end gap-1 shrink-0 min-w-[180px]">
                      <div className="text-emerald-400 font-bold text-lg" data-testid={`history-amount-${h.id}`}>
                        {formatIQD(h.paid_amount)}
                      </div>
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(h.activated_at)}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <UserCheck className="w-3 h-3" />
                        {h.activated_by_name || h.activated_by || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
